/**
 * ThemeAndFavicon Component (Client-side, app-wide)
 *
 * Handles theme switching and favicon updates for the entire application.
 * Included in layout.tsx so it applies to all pages.
 *
 * - Detects initial theme from localStorage or system preference
 * - Updates favicon based on theme using Strapi logo URLs (via proxy)
 * - Applies theme class to document root
 */
"use client";

import { useEffect } from 'react';
import { toMediaProxyPath } from '@/components/toMediaProxyPath';

type ThemeAndFaviconProps = {
  logoLightUrl?: string;
  logoDarkUrl?: string;
};

export default function ThemeAndFavicon({ logoLightUrl, logoDarkUrl }: ThemeAndFaviconProps) {
  useEffect(() => {
    // Determine initial theme
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');

    // Apply theme class to document
    if (initialTheme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }

    // Update favicon based on theme
    const favicon = document.getElementById('site-favicon') as HTMLLinkElement | null;
    if (favicon) {
      const proxiedLogoLightUrl = toMediaProxyPath(logoLightUrl) || '/undefined-logo-light.svg';
      const proxiedLogoDarkUrl = toMediaProxyPath(logoDarkUrl) || '/undefined-logo-dark.svg';
      if (initialTheme === 'light') {
        favicon.href = proxiedLogoLightUrl;
      } else {
        favicon.href = proxiedLogoDarkUrl;
      }
    }
  }, [logoLightUrl, logoDarkUrl]);

  return null; // This component doesn't render anything
}
